/* eslint-disable react/no-unknown-property */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */

import React from 'react';
import {
  Badge,
  Icon,
  ICON_ARROW_UP,
  Message,
  Spinner,
  useCarousel,
} from '@doveit/bricks';

import { Design } from '../../../providers/api/dtos';
import useMarketingDesign from '../../hooks/use-marketing-design/useMarketingDesign';
import CanvaDesignPreview from '../../components/canva-design-preview/CanvaDesignPreview';
import * as styles from './ViewCanvaDesignPreview.style';

export interface ViewCanvaDesignPreviewProps {
  designId: NonNullable<Design['id']>;
}

const DESIGN_PREVIEW_HEIGHT = '25rem';
const MAX_RETRIES = 5;
const RETRY_INTERVAL = 2000;

const ViewCanvaDesignPreview: React.FC<ViewCanvaDesignPreviewProps> = ({
  designId,
}) => {
  const retryCountRef = React.useRef(0);
  const [hasReachedMaxRetries, setHasReachedMaxRetries] = React.useState(false);

  React.useEffect(() => {
    retryCountRef.current = 0;
    setHasReachedMaxRetries(false);
  }, [designId]);

  const {
    data: design,
    isLoading: isDesignLoading,
    error: designError,
  } = useMarketingDesign(designId, {
    dedupingInterval: 0,
    refreshInterval: (latestData) => {
      const pages = latestData?.pages;

      const havePageThumbnails = !!pages?.length
        && pages.every((page) => !!page.thumbnail?.url);

      if (havePageThumbnails) {
        retryCountRef.current = 0;
        setHasReachedMaxRetries(false);

        return 0;
      }

      retryCountRef.current += 1;

      if (retryCountRef.current >= MAX_RETRIES) {
        setHasReachedMaxRetries(true);

        return 0;
      }

      return RETRY_INTERVAL;
    },
  });

  const pageThumbnailsAvailable = !!design?.pages?.length
    && design.pages.every((page) => !!page.thumbnail?.url);

  const shouldUseFallbackThumbnail = hasReachedMaxRetries
    && !pageThumbnailsAvailable
    && !!design?.thumbnail?.url;

  const carouselPages = pageThumbnailsAvailable ? design?.pages ?? [] : [];

  const { activePosition = 0, next, prev } = useCarousel({
    totalElements: carouselPages.length,
    initialActivePosition: 0,
  });

  const hasMorePages = carouselPages.length > 1;

  if (!hasReachedMaxRetries && (isDesignLoading || (!pageThumbnailsAvailable && !designError))) {
    return (
      <div
        css={styles.loadingBox}
        $height={DESIGN_PREVIEW_HEIGHT}
        aria-label="Box di caricamento anteprima del design"
      >
        <Spinner color="neutrals.light" />
      </div>
    );
  }

  if (shouldUseFallbackThumbnail) {
    return (
      <div css={styles.basic}>
        <CanvaDesignPreview
          src={design.thumbnail!.url}
          alt="Anteprima del design"
          height={DESIGN_PREVIEW_HEIGHT}
          boxed
          badgeLabel="Design"
        />
      </div>
    );
  }

  if (designError || (hasReachedMaxRetries && !design?.thumbnail?.url)) {
    return (
      <Message
        type="critical"
        message="Non è stato possibile caricare l'anteprima del design."
        boxed
      />
    );
  }

  if (!carouselPages.length) {
    return null;
  }

  const activePage = carouselPages[activePosition];

  return (
    <div css={styles.basic}>
      {hasMorePages && (
        <div css={styles.navigationStatus}>
          <Badge
            label={`${activePosition + 1} / ${carouselPages.length}`}
            size="XS"
          />
        </div>
      )}

      <CanvaDesignPreview
        src={activePage.thumbnail!.url}
        alt={`Anteprima pagina ${activePosition + 1}`}
        height={DESIGN_PREVIEW_HEIGHT}
        boxed
        badgeLabel="Design"
      />

      {hasMorePages && (
        <div css={styles.navigationButtons}>
          <div
            css={styles.navigationButton}
            onClick={prev}
            aria-label="Pulsante per visualizzare la pagina di design precedente"
          >
            <div>
              <Icon
                path={ICON_ARROW_UP}
                direction="left"
              />
            </div>
          </div>

          <div
            css={styles.navigationButton}
            onClick={next}
            aria-label="Pulsante per visualizzare la pagina di design successiva"
          >
            <div>
              <Icon
                path={ICON_ARROW_UP}
                direction="right"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewCanvaDesignPreview;
